import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronDown, HiArrowRightOnRectangle, HiBell } from 'react-icons/hi2';
import mainLogo from '../../../components/assets/images/new_logo.jpg';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../../../context/AuthContext';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';
import { markDocumentBankViewAsRead } from '../../../services/documentBankService';

const ClientHeader = () => {
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [profilePicture, setProfilePicture] = useState(null);
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);

        try {
          const userDocRef = doc(firestore, 'clients', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setUserProfile(userDocSnap.data());
          } else {
            setUserProfile(null);
          }
        } catch (error) {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const unsubscribers = [];

    if (userProfile?.reports?.length) {
      const feedbackQuery = query(
        collection(firestore, 'feedback'),
        where('reportId', 'in', userProfile.reports),
        where('read', '==', false)
      );

      const messagesQuery = query(
        collection(firestore, 'messages'),
        where('reportId', 'in', userProfile.reports),
        where('userId', '!=', user.uid),
        where('read', '==', false)
      );

      const unsubscribeFeedback = onSnapshot(feedbackQuery, snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            setNotifications(prev => {
              if (prev.some(notification => notification.id === change.doc.id)) return prev;
              return [
                ...prev,
                {
                  id: change.doc.id,
                  type: 'feedback',
                  message: 'New feedback on your report',
                  timestamp: change.doc.data().createdAt,
                  read: false,
                },
              ];
            });
          }
        });
      });

      const unsubscribeMessages = onSnapshot(messagesQuery, snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            setNotifications(prev => {
              if (prev.some(notification => notification.id === change.doc.id)) return prev;
              return [
                ...prev,
                {
                  id: change.doc.id,
                  type: 'message',
                  message: `New message from ${change.doc.data().userName}`,
                  timestamp: change.doc.data().createdAt,
                  read: false,
                },
              ];
            });
          }
        });
      });

      unsubscribers.push(unsubscribeFeedback, unsubscribeMessages);
    }

    const documentViewQuery =
      user?.uid &&
      query(
        collection(firestore, 'documentBankViews'),
        where('clientId', '==', user.uid),
        where('read', '==', false)
      );

    if (documentViewQuery) {
      const unsubscribeViews = onSnapshot(documentViewQuery, snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const data = change.doc.data() || {};
            setNotifications(prev => {
              if (prev.some(notification => notification.id === change.doc.id)) return prev;
              return [
                ...prev,
                {
                  id: change.doc.id,
                  type: 'document-view',
                  message: `${data.viewerName || 'A team member'} viewed your document bank`,
                  timestamp: data.createdAt,
                  read: false,
                },
              ];
            });
          }
        });
      });
      unsubscribers.push(unsubscribeViews);
    }

    // Task notifications - clients see tasks assigned to them
    const tasksQuery = query(
      collection(firestore, 'tasks'),
      where('assignedToEmail', '==', user.email)
    );

    const unsubscribeTasks = onSnapshot(
      tasksQuery,
      snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const taskData = change.doc.data();
            // Only notify for non-completed tasks
            if (taskData.status !== 'completed') {
              setNotifications(prev => {
                if (prev.some(notification => notification.id === `task-${change.doc.id}`))
                  return prev;
                return [
                  ...prev,
                  {
                    id: `task-${change.doc.id}`,
                    type: 'task',
                    message: `New task assigned: ${taskData.title || 'Untitled Task'}`,
                    timestamp: taskData.createdAt || serverTimestamp(),
                    read: false,
                    taskId: change.doc.id,
                  },
                ];
              });
            }
          }
        });
      },
      error => {
        console.error('Task notification listener error:', error);
      }
    );

    unsubscribers.push(unsubscribeTasks);

    // Session notifications - clients see new session notes created for them
    const sessionNotesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('clientId', '==', user?.uid || '')
    );

    const unsubscribeSessionNotes = onSnapshot(
      sessionNotesQuery,
      snapshot => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'added') {
            const sessionData = change.doc.data();
            // Only notify for recent sessions (last 24 hours)
            const sessionTime = sessionData.createdAt?.toDate?.() || new Date();
            const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

            if (sessionTime > oneDayAgo) {
              setNotifications(prev => {
                if (prev.some(notification => notification.id === `session-${change.doc.id}`))
                  return prev;
                return [
                  ...prev,
                  {
                    id: `session-${change.doc.id}`,
                    type: 'session',
                    message: `New session note added: ${sessionData.clientName || 'Session'} - ${sessionData.date ? new Date(sessionData.date).toLocaleDateString() : ''}`,
                    timestamp: sessionData.createdAt || serverTimestamp(),
                    read: false,
                    sessionId: change.doc.id,
                  },
                ];
              });
            }
          }
        });
      },
      error => {
        console.error('Session notification listener error:', error);
      }
    );

    unsubscribers.push(unsubscribeSessionNotes);

    return () => {
      unsubscribers.forEach(unsub => {
        if (typeof unsub === 'function') unsub();
      });
    };
  }, [user, userProfile?.reports]);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  useEffect(() => {
    if (userProfile?.profilePicture) {
      setProfilePicture(userProfile.profilePicture);
    } else {
      setProfilePicture(null);
    }
  }, [userProfile?.profilePicture]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const markNotificationAsRead = async notificationId => {
    const targetNotification = notifications.find(item => item.id === notificationId);
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      )
    );

    if (targetNotification?.type === 'document-view') {
      await markDocumentBankViewAsRead(notificationId);
    }
  };
  if (loading) {
    return (
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-xl font-bold text-black">BDIH</h1>
          </motion.div>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
          <div className="hidden flex-col gap-1 sm:flex">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-200"></div>
            <div className="h-2 w-16 animate-pulse rounded bg-gray-200"></div>
          </div>
        </div>
      </motion.header>
    );
  }

  if (!user) {
    return null;
  }

  const displayName =
    userProfile?.organization && userProfile?.lastname
      ? `${userProfile.organization} `
      : user.displayName || 'User';

  const firstName = userProfile?.organization || user.displayName?.split(' ')[0] || 'User';
  const initials = `${firstName.charAt(0)}`.toUpperCase();

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-xl"
    >
      <div className="flex items-center gap-4">
        <motion.img
          src={mainLogo}
          alt="BDIH Logo"
          className="h-14 w-auto max-w-[180px] object-contain md:h-16"
          whileHover={{ scale: 1.05 }}
          transition={{ type: 'spring', stiffness: 400, damping: 10 }}
        />
      </div>
      <div className="flex items-center gap-4">
        <div className="relative" ref={notificationsRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative rounded-xl p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <HiBell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
                {unreadCount}
              </span>
            )}
          </motion.button>
          <AnimatePresence>
            {notificationsOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-80 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl"
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <h3 className="text-sm font-semibold text-black">Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto py-2">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <motion.div
                        key={notification.id}
                        whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                        onClick={() => markNotificationAsRead(notification.id)}
                        className={`flex cursor-pointer items-start gap-3 px-4 py-3 ${
                          !notification.read ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-sm text-black">{notification.message}</p>
                          <p className="mt-1 text-xs text-gray-500">
                            {notification.timestamp?.toDate
                              ? new Date(notification.timestamp.toDate()).toLocaleString()
                              : notification.timestamp
                                ? new Date(notification.timestamp).toLocaleString()
                                : 'Just now'}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-center text-sm text-gray-500">
                      No notifications
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            onClick={() => setDropdownOpen(open => !open)}
          >
            <div className="relative">
              {profilePicture ? (
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full shadow-lg">
                  <img src={profilePicture} alt="Profile" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-lg">
                  {initials}
                </div>
              )}
              <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500"></div>
            </div>
            <div className="hidden flex-col items-start text-left sm:flex">
              <span className="text-sm font-semibold text-black">{firstName}</span>
              <span className="text-xs text-gray-500">{user.email}</span>
            </div>
            <motion.div animate={{ rotate: dropdownOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <HiChevronDown className="h-4 w-4 text-gray-400" />
            </motion.div>
          </motion.button>
          <AnimatePresence>
            {dropdownOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-xl ring-1 ring-black/5 backdrop-blur-xl"
              >
                <div className="border-b border-gray-100 px-4 py-4">
                  <div className="flex items-center gap-3">
                    {profilePicture ? (
                      <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full shadow-lg">
                        <img
                          src={profilePicture}
                          alt="Profile"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-lg">
                        {initials}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-black">{displayName}</div>
                      <div className="truncate text-xs text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <motion.button
                    whileHover={{ backgroundColor: 'rgb(248 250 252)' }}
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-600 transition-colors"
                  >
                    <HiArrowRightOnRectangle className="h-4 w-4" />
                    <span>Sign out</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </motion.header>
  );
};

export default ClientHeader;
